import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CategoryPills from "@/components/CategoryPills";
import FlipCard from "@/components/FlipCard";
import QuizCard from "@/components/QuizCard";
import SignInPrompt from "@/components/SignInPrompt";
import CaughtUpCard from "@/components/CaughtUpCard";
import BlitzCompleteCard from "@/components/BlitzCompleteCard";
import { publicApi, userApi } from "@/lib/api";
import { QUIZ_EVERY_N, TID } from "@/lib/constants";
import { useAuth } from "@/lib/AuthContext";
import { tickStreak } from "@/lib/streak";
import { getCachedCards, setCachedCards, isCacheableMode } from "@/lib/feedCache";
import { toast } from "sonner";
import { Coffee, Loader2, WifiOff } from "lucide-react";

// Guest-local read storage. Persists across page reloads inside the same
// browser without ever touching the server — a "1-Day Streak Active" phantom
// from the previous release was caused by mixing local + shared state.
const GUEST_READS_KEY = "distill_guest_reads_v1";
function readGuestReads() {
  try {
    const raw = localStorage.getItem(GUEST_READS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (err) {
    console.warn("[feed] guest read set unreadable:", err?.message || err);
    return new Set();
  }
}
function writeGuestReads(set) {
  try {
    localStorage.setItem(GUEST_READS_KEY, JSON.stringify([...set]));
  } catch (err) {
    console.warn("[feed] guest read set save failed:", err?.message || err);
  }
}

// Poll the public feed every 30s to detect freshly published cards without
// forcing a full reload. Only runs while the browser tab is visible.
const POLL_INTERVAL_MS = 30_000;
// Pull-to-refresh threshold — a downward swipe that exceeds this distance from
// the top edge of the scroll container triggers a reload.
const PTR_THRESHOLD_PX = 72;
// Cap the visual arm-drag so the reader never feels the container is broken.
const PTR_MAX_PULL_PX = 110;

export default function Feed() {
  const { user } = useAuth();
  const [category, setCategory] = useState("all");
  const [mode, setMode] = useState("default"); // 'default' | 'revision' | 'history'
  // Hydrate immediately from the local cache so the deck appears without a
  // network round-trip — critical for the first paint on 3G/airplane mode.
  // We only cache the default feed (see feedCache.isCacheableMode).
  const [cards, setCards] = useState(() => {
    const cached = getCachedCards("all", "default");
    return cached ? cached.cards : [];
  });
  // `loading` is only true when we have NO content to show yet. If the cache
  // already handed us cards, we skip the skeleton — the user sees instant UI
  // and the fresh fetch merges in silently.
  const [loading, setLoading] = useState(() => {
    const cached = getCachedCards("all", "default");
    return !cached;
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [weakCount, setWeakCount] = useState(0);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInAction, setSignInAction] = useState("save this card");
  // Real-time freshness signal — the newest card_id detected by the poller
  // that is NOT already in our loaded stack. When set, the "Freshly brewed"
  // toast is rendered.
  const [freshHint, setFreshHint] = useState(null); // { card_id, published_at } | null
  const [merging, setMerging] = useState(false);
  // Pull-to-refresh visual state — pull distance in px + refreshing spinner.
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // Offline-mode visual state. `offline` becomes true when the latest fetch
  // failed AND we have cached cards to keep showing. It flips back to false
  // the moment any subsequent fetch succeeds OR the browser emits an `online`
  // event followed by a successful refetch.
  const [offline, setOffline] = useState(false);

  // Track which card IDs have already been reported as read this session so we
  // don't spam the endpoint if the same card scrolls back into view.
  const readReported = useRef(new Set());
  const containerRef = useRef(null);
  const pillsRef = useRef(null);
  const sentinelRef = useRef(null);
  // Ref-mirrors of state used by touch handlers (avoid stale closures).
  const pullActive = useRef(false);
  const pullStartY = useRef(0);
  // Server-side batch size (mirrors backend default limit).
  const BATCH_SIZE = 15;

  const loadCards = useCallback(async () => {
    // Hydrate from the local cache FIRST so the reader sees cards instantly
    // on filter switches / cold starts. Only applies to the default feed —
    // revision + history modes always hit the network for correctness.
    const cached = isCacheableMode(mode) ? getCachedCards(category, mode) : null;
    if (cached) {
      setCards(cached.cards);
      setLoading(false);
    } else {
      // No cache for this filter — show the skeleton like before.
      setLoading(true);
    }

    try {
      const data = await publicApi.listCards({ category, mode, limit: BATCH_SIZE, skip: 0 });
      const fresh = data.cards || [];
      setCards(fresh);
      setHasMore(!!data.has_more);
      setWeakCount(data.weak_count || 0);
      // Fetch succeeded → network is healthy, drop any offline indicator and
      // persist the new deck for next cold start.
      setOffline(false);
      setCachedCards(category, mode, fresh);
      // Revision mode may return a "not-yet-eligible" cold-start payload.
      if (mode === "revision" && data.eligible === false) {
        if (data.reason === "cold_start") {
          toast.info(`Read ${data.reads_needed || 3} more cards to unlock Revision Blitz.`);
        } else if (data.reason === "auth_required") {
          toast.info("Sign in to use Revision Blitz.");
        }
        setMode("default");
      }
    } catch (e) {
      console.error(e);
      // Graceful degradation. If the cache gave us cards (or we already had
      // some in state from a previous successful load), KEEP them and show a
      // subtle "Offline" banner. Only surface the error toast when the deck
      // is actually empty — that's the only time the reader is truly stuck.
      if (cached || cards.length > 0) {
        setOffline(true);
      } else {
        toast.error("Failed to load feed");
      }
    } finally {
      setLoading(false);
    }
    // We intentionally omit `cards.length` from deps — reading it fresh inside
    // the catch branch is safe (we only read, never create a closure loop),
    // and including it would re-create loadCards on every append.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, mode]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const data = await publicApi.listCards({
        category,
        mode,
        limit: BATCH_SIZE,
        skip: cards.length,
      });
      const next = data.cards || [];
      // De-dupe on card_id — safeguards against a race where two batches overlap
      // after the reader logs a card as read mid-fetch.
      setCards((prev) => {
        const seen = new Set(prev.map((c) => c.card_id));
        const merged = [...prev];
        for (const c of next) {
          if (!seen.has(c.card_id)) merged.push(c);
        }
        // Re-cache the accumulated deck so a later cold start / airplane-mode
        // reload shows everything the reader has seen so far, not just the
        // first page.
        setCachedCards(category, mode, merged);
        return merged;
      });
      setHasMore(!!data.has_more);
      // A successful loadMore implicitly proves we're online — drop any
      // lingering offline banner from a previous failed loadCards.
      setOffline(false);
    } catch (err) {
      console.warn("[feed] load-more failed:", err?.message || err);
      // Pagination failure is non-fatal; the primary deck is still shown.
      // Surface the offline state only if the browser agrees we're offline,
      // to avoid false alarms on a one-off transient network blip.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setOffline(true);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [category, mode, cards.length, hasMore, loadingMore]);

  const loadBookmarks = useCallback(async () => {
    if (!user) { setBookmarkedIds(new Set()); return; }
    try {
      const data = await userApi.listBookmarks();
      setBookmarkedIds(new Set((data.items || []).map((i) => i.card.card_id)));
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[feed] Bookmark fetch failed:", err.response.status, err?.response?.data);
      }
      setBookmarkedIds(new Set());
    }
  }, [user]);

  // ------------------------------------------------------------------
  // Real-time freshness poll — every 30s, ask the backend for the top
  // card in the reader's current filter. If it's not already in our
  // loaded stack, surface the "Freshly brewed" toast without reloading.
  // ------------------------------------------------------------------
  useEffect(() => {
    // Revision mode is a fixed historical slice; polling is meaningless there.
    if (mode !== "default") return undefined;

    let cancelled = false;

    const poll = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const data = await publicApi.listCards({ category, mode: "default", limit: 1, skip: 0 });
        if (cancelled) return;
        const top = (data.cards || [])[0];
        if (!top) { setFreshHint(null); return; }
        // If the top-of-feed card_id is already in our loaded stack, nothing new.
        const loadedIds = new Set(cards.map((c) => c.card_id));
        if (loadedIds.has(top.card_id)) { setFreshHint(null); return; }
        // Otherwise a genuinely new card has appeared — surface the toast.
        setFreshHint({ card_id: top.card_id, published_at: top.published_at });
      } catch (err) {
        // Silent — polling failures are non-fatal, we'll retry next tick.
        console.debug("[feed] poll failed:", err?.message || err);
      }
    };

    const id = setInterval(poll, POLL_INTERVAL_MS);
    // Fire once shortly after mount so a reader who opens the app doesn't
    // wait 30s for the first check.
    const kickoff = setTimeout(poll, 4_000);

    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(kickoff);
    };
  }, [category, mode, cards]);

  // "Tap to sip" — pull the latest batch and prepend anything new to the top
  // of the stack. No page reload, no scroll disturbance mid-stack.
  const sipFreshCards = useCallback(async () => {
    if (merging) return;
    setMerging(true);
    setFreshHint(null);
    try {
      const data = await publicApi.listCards({ category, mode: "default", limit: BATCH_SIZE, skip: 0 });
      const next = data.cards || [];
      setCards((prev) => {
        const seen = new Set(prev.map((c) => c.card_id));
        const additions = next.filter((c) => !seen.has(c.card_id));
        if (additions.length === 0) return prev;
        toast.success(`${additions.length} fresh card${additions.length === 1 ? "" : "s"} added`);
        const merged = [...additions, ...prev];
        // Cache the prepended deck so the next cold start opens with the
        // freshest cards on top instead of the old first page.
        setCachedCards(category, "default", merged);
        return merged;
      });
      setHasMore(!!data.has_more);
      setWeakCount(data.weak_count || 0);
      setOffline(false);
      // Nudge the reader to the top so they see the new cards land.
      if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.warn("[feed] sip failed:", err?.message || err);
      // Don't scare the reader with a toast when they're clearly offline —
      // the banner already tells the story. Only toast on genuine failures
      // while the browser believes the network is up.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setOffline(true);
      } else {
        toast.error("Couldn't fetch fresh cards. Try again in a moment.");
      }
    } finally {
      setMerging(false);
    }
  }, [category, merging]);

  // ------------------------------------------------------------------
  // Pull-to-refresh (mobile only). Detects a downward drag beginning at
  // scrollTop === 0 and triggers a full reload once the arm-drag crosses
  // PTR_THRESHOLD_PX. Pointer-friendly: only fires for touch inputs so
  // desktop wheel scrolls aren't hijacked.
  // ------------------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (el.scrollTop > 0) { pullActive.current = false; return; }
      pullActive.current = true;
      pullStartY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (!pullActive.current) return;
      const dy = e.touches[0].clientY - pullStartY.current;
      if (dy <= 0) { setPullDistance(0); return; }
      // Rubberband: distance grows sub-linearly so the pull feels physical.
      const damped = Math.min(PTR_MAX_PULL_PX, dy * 0.5);
      setPullDistance(damped);
    };
    const onTouchEnd = async () => {
      const dist = pullDistance;
      pullActive.current = false;
      if (dist >= PTR_THRESHOLD_PX && !refreshing) {
        setRefreshing(true);
        try { await sipFreshCards(); }
        finally { setRefreshing(false); }
      }
      setPullDistance(0);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pullDistance, refreshing, sipFreshCards]);

  useEffect(() => { loadCards(); }, [loadCards]);
  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  // Auto-reconnect — when the browser regains connectivity, silently refetch
  // the current deck. The user never has to touch pull-to-refresh or restart
  // the app. `offline` events also update the badge, so the banner never
  // lingers after the network is restored.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      // Only refetch when we were actually in offline-degraded mode. Otherwise
      // a transient `online` event (e.g., VPN flap) would spam the API.
      if (offline) {
        loadCards();
      }
    };
    const handleOffline = () => {
      // If the browser tells us we're offline AND we have cards to show,
      // surface the banner immediately — don't wait for the next failed fetch.
      if (cards.length > 0) setOffline(true);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // `cards.length` is deliberately captured by value for the offline handler;
    // we don't need to re-bind on every append. `loadCards` is stable per
    // (category, mode), which is the correct retry granularity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offline, loadCards]);

  // Reset "already-reported" set whenever the queue itself changes — otherwise
  // switching filters could re-mark cards without re-triggering the observer.
  useEffect(() => {
    readReported.current = new Set();
  }, [category, mode]);

  // Compose feed with quiz insertions every N cards
  const feedItems = useMemo(() => {
    const out = [];
    let quizCounter = 0;
    for (let i = 0; i < cards.length; i++) {
      out.push({ type: "card", card: cards[i] });
      if (cards[i].quiz && quizCounter >= QUIZ_EVERY_N - 1) {
        out.push({ type: "quiz", quiz: cards[i].quiz, card_id: cards[i].card_id, seed: `${cards[i].card_id}` });
        quizCounter = 0;
      } else {
        quizCounter += 1;
      }
    }
    return out;
  }, [cards]);

  // The completion card takes over the empty state entirely (no more
  // "No cards in this filter" dead ends). It renders when:
  //   • the reader is on the default feed (revision mode has its own empty state)
  //   • AND the current queue has been fully cleared — either the feed loaded 0
  //     unread cards, or the reader has scrolled through everything with no more
  //     batches available from the server.
  // Works for guests too — the Revision Blitz button is hidden for them and
  // pressing Browse Subject Vault surfaces the category rail.
  const feedCleared =
    !loading &&
    !loadingMore &&
    !hasMore &&
    mode === "default" &&
    (feedItems.length === 0 || (feedItems.length > 0 && user && category === "all"));

  // Revision mode has its own empty-state messaging (e.g., "Nothing to revise
  // yet") — keep that as-is. Default-mode emptiness is handled by feedCleared.
  const showRevisionEmpty =
    !loading && feedItems.length === 0 && mode === "revision";

  // ------------------------------------------------------------------
  // Read-tracking IntersectionObserver
  // Fires for BOTH guest and authed readers. Guests: writes to localStorage
  // so their history persists per-browser without a server-side shared record.
  // Authed: writes server-side via userApi.markCardRead. Both cases tick the
  // Deep Work Streak so a single card scroll = day counted.
  // ------------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // A card counts as "read" once ≥65% of it has been in the viewport for
    // at least ~1.2s. This filters out flick-throughs and observers firing
    // during initial layout.
    const dwellTimers = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const cardId = el.getAttribute("data-card-read-id");
        if (!cardId) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (readReported.current.has(cardId)) return;
          if (dwellTimers.has(cardId)) return;
          const t = setTimeout(() => {
            if (!readReported.current.has(cardId)) {
              readReported.current.add(cardId);
              // Engagement tick — bumps streak only when the reader actually
              // dwells on a card, not merely opens the app.
              try { tickStreak(); } catch (err) { void err; }
              if (user) {
                userApi.markCardRead(cardId); // fire-and-forget
              } else {
                // Guest: persist to localStorage. No server call.
                const gset = readGuestReads();
                gset.add(cardId);
                writeGuestReads(gset);
              }
            }
            dwellTimers.delete(cardId);
          }, 1200);
          dwellTimers.set(cardId, t);
        } else {
          const t = dwellTimers.get(cardId);
          if (t) { clearTimeout(t); dwellTimers.delete(cardId); }
        }
      });
    }, {
      root: container,
      threshold: [0, 0.6, 1],
    });

    const sections = container.querySelectorAll("[data-card-read-id]");
    sections.forEach((s) => observer.observe(s));

    return () => {
      observer.disconnect();
      dwellTimers.forEach((t) => clearTimeout(t));
      dwellTimers.clear();
    };
  }, [user, feedItems]);

  // ------------------------------------------------------------------
  // Infinite-scroll sentinel — auto-fetch next batch when the reader
  // approaches the tail. The sentinel sits inside the scroll container
  // so we can trigger the fetch before the reader hits the visual end.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (loading || !hasMore) return;
    const container = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e && e.isIntersecting) loadMore();
    }, { root: container, rootMargin: "600px 0px", threshold: 0 });
    io.observe(sentinel);
    return () => io.disconnect();
  }, [loading, hasMore, loadMore]);

  const onToggleBookmark = async (card, isCurrentlyBookmarked) => {
    if (!user) {
      setSignInAction("save this card");
      setSignInOpen(true);
      return;
    }
    try {
      if (isCurrentlyBookmarked) {
        await userApi.removeBookmark(card.card_id);
        setBookmarkedIds((prev) => { const n = new Set(prev); n.delete(card.card_id); return n; });
        toast("Removed from Saved");
      } else {
        await userApi.addBookmark(card.card_id);
        setBookmarkedIds((prev) => { const n = new Set(prev); n.add(card.card_id); return n; });
        toast.success("Saved. Auto-purges in 10 days.");
      }
    } catch (e) {
      toast.error("Bookmark failed. Try again.");
    }
  };

  const requireAuthForQuiz = () => {
    setSignInAction("attempt the quiz");
    setSignInOpen(true);
  };

  const launchRevision = () => {
    if (!user) {
      setSignInAction("open Revision Blitz");
      setSignInOpen(true);
      return;
    }
    setMode("revision");
    setCategory("all");
    // Scroll back to top so the first revision card lands under the user's thumb.
    if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const exploreVault = () => {
    // Legacy handler kept for accessibility; the "Browse Subject Vault" button
    // is no longer part of the CaughtUpCard. If invoked programmatically, it
    // still nudges the reader to the category rail.
    if (pillsRef.current) {
      pillsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      pillsRef.current.classList.add("ring-2", "ring-[#F2BD68]");
      setTimeout(() => {
        pillsRef.current && pillsRef.current.classList.remove("ring-2", "ring-[#F2BD68]");
      }, 1400);
    }
    setMode("default");
    setCategory("gs");
  };

  const reviewAllReadCards = () => {
    if (!user) {
      setSignInAction("review your read history");
      setSignInOpen(true);
      return;
    }
    setMode("history");
    setCategory("all");
    if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const exitHistory = () => {
    setMode("default");
    setCategory("all");
  };

  const exitRevision = () => {
    setMode("default");
    setCategory("all");
  };

  // Blitz completion handlers — used by the tail card after 10/10.
  const runAnotherBlitz = async () => {
    // Force a fresh 10-card weighted deck. Same mode; the backend picks a
    // new sample every time.
    if (containerRef.current) containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    await loadCards();
  };
  const exitBlitz = () => {
    setMode("default");
    setCategory("all");
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      {/* Constrained container: on desktop the feed keeps a mobile-portrait feel
          instead of stretching across a widescreen. `max-w-[480px]` gives us a
          natural phone-like column while still respecting `100dvh` on real mobile. */}
      <div className="mx-auto w-full max-w-[480px] sm:max-w-[520px]">
        {/* Freshly brewed toast — floats above the sticky header when new content arrives */}
        {freshHint && (
          <div
            data-testid="feed-fresh-toast"
            className="fixed left-1/2 top-3 z-40 -translate-x-1/2 w-[min(94vw,420px)]"
            role="status"
            aria-live="polite"
          >
            <button
              type="button"
              onClick={sipFreshCards}
              disabled={merging}
              className="w-full inline-flex items-center justify-between gap-3 rounded-2xl border border-[#F2BD68]/45 bg-gradient-to-r from-[#1A2232] via-[#232E45] to-[#1A2232] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:border-[#F2BD68] active:scale-[0.99] transition-all disabled:opacity-70"
            >
              <span className="inline-flex items-center gap-2">
                {merging ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[#F2BD68]" aria-hidden />
                ) : (
                  <Coffee className="h-4 w-4 text-[#F2BD68]" aria-hidden />
                )}
                <span>Freshly brewed current affairs.</span>
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F2BD68]">
                {merging ? "Sipping…" : "Tap to sip"}
              </span>
            </button>
          </div>
        )}

        <div
          ref={pillsRef}
          className="sticky top-0 z-20 bg-[#111622]/95 backdrop-blur supports-[backdrop-filter]:bg-[#111622]/80 border-b border-[#26334D] rounded-b-md transition-shadow"
        >
          {/* Subtle offline indicator — only visible when the latest fetch
              failed but the cache is still serving cards. Non-intrusive strip
              that sits above the category rail; dismisses itself the moment a
              refetch succeeds (via the `online` listener below). */}
          {offline && (
            <div
              data-testid="feed-offline-banner"
              role="status"
              aria-live="polite"
              className="flex items-center justify-center gap-2 px-4 py-1.5 text-[11px] font-semibold tracking-[0.08em] bg-[#1A2232] border-b border-[#F2BD68]/25 text-[#F2BD68]"
            >
              <WifiOff className="h-3 w-3" aria-hidden />
              <span>Offline &middot; Showing saved notes</span>
            </div>
          )}
          <CategoryPills
            value={category}
            onChange={(next) => { setCategory(next); if (mode === "revision") setMode("default"); }}
          />
          {mode === "revision" && (
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-2 bg-[#F2BD68]/10 border-t border-[#F2BD68]/25">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F2BD68]">
                Revision Blitz &middot; Card {Math.min(cards.length || 0, 10)} of 10
                {weakCount > 0 && (
                  <span
                    className="ml-2 inline-flex items-center rounded-full bg-[#E85A2A]/15 border border-[#E85A2A]/40 px-1.5 py-0.5 text-[10px] font-semibold text-[#E85A2A] tracking-wide"
                    data-testid="feed-weak-spot-badge"
                  >
                    {weakCount} weak spot{weakCount === 1 ? "" : "s"}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={exitRevision}
                data-testid="feed-exit-revision"
                className="text-[11px] font-semibold text-[#F2BD68] hover:underline"
              >
                Exit Blitz
              </button>
            </div>
          )}
          {mode === "history" && (
            <div
              data-testid="feed-history-banner"
              className="flex items-center justify-between gap-2 px-4 sm:px-6 py-2 bg-[#26334D]/60 border-t border-[#26334D]"
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F2BD68]">
                Viewing Read History
              </span>
              <button
                type="button"
                onClick={exitHistory}
                data-testid="feed-exit-history"
                className="text-[11px] font-semibold text-[#F2BD68] hover:underline"
              >
                Back to Unread
              </button>
            </div>
          )}
        </div>

        <div
          ref={containerRef}
          data-testid={TID.feedContainer}
          className="relative snap-y-mandatory no-scrollbar overflow-y-auto overscroll-contain"
          style={{ height: "calc(100dvh - 56px - 55px - 76px - env(safe-area-inset-bottom))" }}
        >
          {/* Pull-to-refresh visual — shows only while the user is arm-dragging or a refresh is mid-flight. */}
          {(pullDistance > 0 || refreshing) && (
            <div
              data-testid="feed-ptr-indicator"
              className="absolute left-0 right-0 top-0 z-10 flex items-center justify-center pointer-events-none"
              style={{ height: Math.max(pullDistance, refreshing ? 44 : 0), transition: refreshing ? "height 180ms ease" : "none" }}
              aria-hidden={!refreshing}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-[#26334D] bg-[#1A2232]/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F2BD68] shadow-[0_6px_18px_rgba(0,0,0,0.35)]">
                {refreshing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Refreshing
                  </>
                ) : pullDistance >= PTR_THRESHOLD_PX ? (
                  <>
                    <Coffee className="h-3.5 w-3.5" aria-hidden />
                    Release to refresh
                  </>
                ) : (
                  <>
                    <Coffee className="h-3.5 w-3.5" aria-hidden />
                    Pull to refresh
                  </>
                )}
              </div>
            </div>
          )}
          {loading ? (
            <div className="px-4 sm:px-6 py-8">
              {[0, 1, 2].map((k) => (
                <div key={k} className="rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 mb-4 animate-pulse">
                  <div className="h-40 rounded-xl bg-[#26334D]" />
                  <div className="mt-4 h-4 w-3/4 rounded bg-[#26334D]" />
                  <div className="mt-2 h-3 w-full rounded bg-[#26334D]" />
                  <div className="mt-2 h-3 w-5/6 rounded bg-[#26334D]" />
                </div>
              ))}
            </div>
          ) : showRevisionEmpty ? (
            /* Revision mode empty — no cards to revise (yet). */
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <p className="text-base font-semibold text-[#F8FAFC]">Nothing to revise yet.</p>
              <p className="mt-2 text-sm text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                Revision Blitz surfaces cards you read 3&ndash;7 days ago. Come back after a week of steady swiping.
              </p>
              <button
                type="button"
                onClick={exitRevision}
                className="mt-4 rounded-xl bg-[#E85A2A] px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#C94A1D] transition-colors"
              >
                Back to today&rsquo;s feed
              </button>
            </div>
          ) : feedItems.length === 0 ? (
            /* Default-mode empty — render the completion card immediately so the
               reader never hits a "No cards in this filter" dead end. */
            <section
              className="snap-start px-3 sm:px-6 py-3 flex items-stretch justify-center"
              style={{ height: "calc(100dvh - 56px - 55px - 76px - env(safe-area-inset-bottom))" }}
            >
              {mode === "history" ? (
                <div className="text-center px-6 self-center">
                  <p className="text-base font-semibold text-[#F8FAFC]">No read history yet.</p>
                  <p className="mt-2 text-sm text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                    Cards you swipe past will show up here.
                  </p>
                </div>
              ) : (
                <CaughtUpCard
                  onRevision={launchRevision}
                  onReviewHistory={reviewAllReadCards}
                  showRevision={!!user}
                />
              )}
            </section>
          ) : (
            <>
              {feedItems.map((item, idx) => (
                <section
                  key={item.type === "card" ? item.card.card_id : `q-${item.seed}-${idx}`}
                  className="snap-start px-3 sm:px-6 py-3 flex items-stretch justify-center"
                  style={{ height: "calc(100dvh - 56px - 55px - 76px - env(safe-area-inset-bottom))" }}
                  {...(item.type === "card" ? { "data-card-read-id": item.card.card_id } : {})}
                >
                  {item.type === "card" ? (
                    <FlipCard
                      card={item.card}
                      isBookmarked={bookmarkedIds.has(item.card.card_id)}
                      onToggleBookmark={onToggleBookmark}
                    />
                  ) : (
                    <QuizCard
                      quiz={item.quiz}
                      cardId={item.card_id}
                      isAuthed={!!user}
                      onRequireAuth={requireAuthForQuiz}
                      onAttempt={async (payload) => {
                        try { await userApi.quizAttempt(payload); }
                        catch (err) {
                          console.warn("[feed] Quiz attempt log failed:", err?.response?.status || err?.message);
                        }
                      }}
                      onSkip={() => {
                        const container = document.querySelector(`[data-testid="${TID.feedContainer}"]`);
                        if (container) container.scrollBy({ top: window.innerHeight, behavior: "smooth" });
                      }}
                    />
                  )}
                </section>
              ))}

              {/* Tail-of-stack completion card — appears after the reader
                  swipes past the last unread card in the default feed. */}
              {feedCleared && (
                <section
                  className="snap-start px-3 sm:px-6 py-3 flex items-stretch justify-center"
                  style={{ height: "calc(100dvh - 56px - 55px - 76px - env(safe-area-inset-bottom))" }}
                >
                  <CaughtUpCard
                    onRevision={launchRevision}
                    onReviewHistory={reviewAllReadCards}
                    showRevision={!!user}
                  />
                </section>
              )}

              {/* Blitz completion card — appears after the reader finishes the
                  10-card Revision Blitz micro-burst. */}
              {mode === "revision" && !loading && feedItems.length > 0 && (
                <section
                  className="snap-start px-3 sm:px-6 py-3 flex items-stretch justify-center"
                  style={{ height: "calc(100dvh - 56px - 55px - 76px - env(safe-area-inset-bottom))" }}
                >
                  <BlitzCompleteCard
                    onRunAnother={runAnotherBlitz}
                    onBackToFeed={exitBlitz}
                    weakCount={weakCount}
                  />
                </section>
              )}

              {/* Infinite-scroll sentinel — triggers the next batch fetch when
                  visible. Rendered only while there is more content to fetch. */}
              {hasMore && (
                <div
                  ref={sentinelRef}
                  data-testid="feed-infinite-sentinel"
                  className="flex items-center justify-center py-6"
                  aria-hidden={!loadingMore}
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2 text-[11px] text-[#94A3B8] uppercase tracking-[0.14em]">
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-[#26334D] border-t-[#E85A2A] animate-spin" />
                      Loading more
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#64748B]">&nbsp;</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SignInPrompt
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        action={signInAction}
        context={{ redirect: "/" }}
      />
    </div>
  );
}
